import { useEffect, useState } from 'react';

export interface ChartTokens {
  openai: string;
  elevenlabs: string;
  claude: string;
  google: string;
  fallback: string;
  grid: string;
  cardBg: string;
  border: string;
  fg: string;
  muted: string;
}

export const FALLBACK_CHART_TOKENS: ChartTokens = {
  openai: '#22c55e',
  elevenlabs: '#8b5cf6',
  claude: '#a855f7',
  google: '#3b82f6',
  fallback: '#94a3b8',
  grid: 'hsl(214.3 31.8% 91.4%)',
  cardBg: 'hsl(0 0% 100%)',
  border: 'hsl(214.3 31.8% 91.4%)',
  fg: 'hsl(222.2 84% 4.9%)',
  muted: 'hsl(215.4 16.3% 46.9%)',
};

export function readChartTokens(): ChartTokens {
  if (typeof document === 'undefined') return FALLBACK_CHART_TOKENS;
  const cs = getComputedStyle(document.documentElement);
  const v = (k: string, fallback: string) => (cs.getPropertyValue(k).trim() || fallback);
  const hsl = (k: string, fallback: string) => {
    const raw = cs.getPropertyValue(k).trim();
    return raw ? `hsl(${raw})` : fallback;
  };
  return {
    openai:     v('--chart-openai',     FALLBACK_CHART_TOKENS.openai),
    elevenlabs: v('--chart-elevenlabs', FALLBACK_CHART_TOKENS.elevenlabs),
    claude:     v('--chart-claude',     FALLBACK_CHART_TOKENS.claude),
    google:     v('--chart-google',     FALLBACK_CHART_TOKENS.google),
    fallback:   v('--chart-fallback',   FALLBACK_CHART_TOKENS.fallback),
    grid:       v('--chart-grid',       FALLBACK_CHART_TOKENS.grid),
    cardBg:     hsl('--card',             FALLBACK_CHART_TOKENS.cardBg),
    border:     hsl('--border',           FALLBACK_CHART_TOKENS.border),
    fg:         hsl('--foreground',       FALLBACK_CHART_TOKENS.fg),
    muted:      hsl('--muted-foreground', FALLBACK_CHART_TOKENS.muted),
  };
}

export function tokensEqual(a: ChartTokens, b: ChartTokens): boolean {
  return a.openai === b.openai && a.elevenlabs === b.elevenlabs
    && a.claude === b.claude && a.google === b.google && a.fallback === b.fallback
    && a.grid === b.grid && a.cardBg === b.cardBg && a.border === b.border
    && a.fg === b.fg && a.muted === b.muted;
}

export function useChartTokens(): ChartTokens {
  const [tokens, setTokens] = useState<ChartTokens>(() => readChartTokens());
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const sync = () => setTokens(prev => {
      const next = readChartTokens();
      return tokensEqual(prev, next) ? prev : next;
    });
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(html, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => obs.disconnect();
  }, []);
  return tokens;
}
